import { create } from "zustand";

export interface DevErrorJson {
  message: string;
  filename: string;
  stack: string;
  count: number;

  lineno?: number;
  colno?: number;
  storyboards?: string[];
  suggestion?: string;
}

export enum ServerCommandType {
  RUN_CONTAINER = "RUN_CONTAINER",
  START_SERVER = "START_SERVER",
}

export interface RunningServerCommand {
  commandType: ServerCommandType;
  startTime: Date;
  abortController?: AbortController;
}

interface DevServerStore {
  terminalErrors: DevErrorJson[];
  addTerminalErrors: (terminalErrors: DevErrorJson[]) => void;
  clearTerminalErrors: () => void;

  storyboardIdToStatus: {
    [storyboardId: string]: {
      status: "offline" | "frozen" | "live" | "loading";
      last_updated: Date;
    };
  };
  setStoryboardIdToStatus: (
    storyboardId: string,
    newStatus: {
      status: "offline" | "frozen" | "live" | "loading";
      last_updated: Date;
    }
  ) => void;

  storyboardIdToError: {
    [storyboardId: string]: DevErrorJson;
  };
  setStoryboardIdToError: (storyboardId: string, error?: DevErrorJson) => void;
  clearStoryboardIdToErrors: () => void;

  resetDevServerStore: () => void;

  // To track if there is any command that is starting the server or not
  runningServerCommands: RunningServerCommand[];
  setRunningServerCommands: (commands: RunningServerCommand[]) => void;
}

export const useDevServerStore = create<DevServerStore>((set, get) => ({
  terminalErrors: [],
  addTerminalErrors: (terminalErrors: DevErrorJson[]) => {
    set({ terminalErrors: [...get().terminalErrors, ...terminalErrors] });
  },
  clearTerminalErrors: () => {
    set({ terminalErrors: [] });
  },

  storyboardIdToStatus: {},
  setStoryboardIdToStatus: (
    storyboardId: string,
    newStatus: {
      status: "offline" | "frozen" | "live" | "loading";
      last_updated: Date;
    }
  ) => {
    set((state) => ({
      storyboardIdToStatus: {
        ...state.storyboardIdToStatus,
        [storyboardId]: newStatus,
      },
    }));
  },

  storyboardIdToError: {},
  setStoryboardIdToError: (storyboardId: string, error?: DevErrorJson) => {
    if (error === undefined) {
      set((state) => {
        const newErrors = { ...state.storyboardIdToError };
        delete newErrors[storyboardId];
        return { storyboardIdToError: newErrors };
      });
    } else {
      set((state) => ({
        storyboardIdToError: {
          ...state.storyboardIdToError,
          [storyboardId]: error,
        },
      }));
    }
  },
  clearStoryboardIdToErrors: () => {
    set({ storyboardIdToError: {} });
  },

  resetDevServerStore: () => {
    set({
      terminalErrors: [],
      storyboardIdToStatus: {},
      storyboardIdToError: {},
      runningServerCommands: [],
    });
  },

  runningServerCommands: [],
  setRunningServerCommands: (commands: RunningServerCommand[]) => {
    set({ runningServerCommands: commands });
  },
}));
